import React from "react";
import PageMeta from "../../components/common/PageMeta";
import AuthLayout from "./AuthPageLayout";
import SignInForm from "../../components/auth/SignInForm";
import { useTranslation } from "react-i18next";
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Capacitor } from "@capacitor/core";
import { hydrateNativeSessionToFirebaseJs } from "../../services/nativeAuthHydrationService";

const SignIn: React.FC = () => {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const [showFeedbackModal, setShowFeedbackModal] = React.useState(false);
  const [feedbackEmail, setFeedbackEmail] = React.useState('');
  const [feedbackMessage, setFeedbackMessage] = React.useState('');
  const [feedbackLoading, setFeedbackLoading] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        if (user) return;
        try {
          if (localStorage.getItem('force_signout') === '1') return;
        } catch {
        }
        const platform = Capacitor.getPlatform();
        if (platform === 'ios') {
          await hydrateNativeSessionToFirebaseJs();
        }
      } catch {
      }
    })();
  }, [user?.uid]);

  if (!loading && user) {
    return <Navigate to="/groups/home-groups" replace />;
  }

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedbackLoading(true);
    
    try {
      await addDoc(collection(db, 'feedback'), {
        email: feedbackEmail,
        message: feedbackMessage,
        source: 'signin',
        createdAt: serverTimestamp(),
        status: 'unread',
      });
      
      alert(t('auth.feedbackSuccess'));
      setFeedbackEmail('');
      setFeedbackMessage('');
      setShowFeedbackModal(false);
    } catch (error) {
      alert(t('auth.feedbackError'));
    } finally {
      setFeedbackLoading(false);
    }
  };

  const SignInRightPanel = (
    <div className="relative flex items-center justify-center z-10 w-full h-full p-6 lg:p-8 xl:p-12">
      <div className="max-w-lg w-full">
        {/* Logo */}
        <div className="mb-6 text-center">
          <img
            src="/images/logo/logo-dark.svg"
            alt="Logrit.Life"
            className="h-10 lg:h-12 mx-auto drop-shadow-lg"
          />
        </div>

        {/* Welcome Back Section */}
        <div className="mb-6 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">
            {t('auth.welcomeBack')} 👋
          </h2>
          <p className="text-base lg:text-lg text-white/90 leading-relaxed">
            {t('auth.welcomeBackDesc')}
          </p>
        </div>

        {/* Stats/Highlights */}
        <div className="space-y-3 mb-6">
          <div className="p-4 rounded-[28px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-white/20" style={{ backgroundColor: '#aad08cff' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex-shrink-0 bg-white/30 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-white font-black text-base">{t('auth.accesorapido')}</h3>
                <p className="text-white/80 text-sm font-semibold">{t('auth.quickAccessDesc')}</p>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-[28px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-white/20" style={{ backgroundColor: '#fdbc52ff' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex-shrink-0 bg-white/30 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-white font-black text-base">{t('auth.needHelp')}</h3>
                <p className="text-white/80 text-sm font-semibold">{t('auth.needHelpDesc')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Feedback CTA */}
        <div className="p-5 rounded-[28px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-white/20" style={{ backgroundColor: '#ef6e6bff' }}>
          <div className="text-center">
            <div className="w-12 h-12 bg-white/30 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
            <h3 className="text-white font-black text-base lg:text-lg mb-2">
              {t('auth.feedbackQuestion')}
            </h3>
            <p className="text-white/80 text-sm font-semibold mb-3">
              {t('auth.feedbackDesc')}
            </p>
            <button
              onClick={() => setShowFeedbackModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/30 hover:bg-white/40 text-white font-black rounded-2xl transition-all border border-white/30 text-sm shadow-[0_4px_12px_rgba(0,0,0,0.15)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.2)]"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {t('auth.sendFeedback')}
            </button>
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowFeedbackModal(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-[28px] max-w-md w-full p-6 shadow-[0_10px_30px_-5px_rgba(5,85,144,0.04)]" style={{ backgroundColor: '#fff5ebff' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-black text-gray-900 dark:text-white">{t('auth.enviarfeedback')}</h3>
              <button
                onClick={() => setShowFeedbackModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleFeedbackSubmit}>
              <div className="mb-4">
                <label className="block text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5 ml-1">
                  {t('auth.yourEmail')}
                </label>
                <input
                  type="email"
                  value={feedbackEmail}
                  onChange={(e) => setFeedbackEmail(e.target.value)}
                  required
                  placeholder="tu@email.com"
                  className="w-full h-[42px] px-4 rounded-full border-none bg-gray-100/50 dark:bg-gray-800/60 focus:ring-2 focus:ring-emerald-500/25 transition-all outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                />
              </div>
              <div className="mb-4">
                <label className="block text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5 ml-1">
                  {t('auth.yourMessage')}
                </label>
                <textarea
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  required
                  rows={5}
                  placeholder={t('auth.cuentanostuexperienciasugerenciasoreporta')}
                  className="w-full px-4 py-3 rounded-2xl border-none bg-gray-100/50 dark:bg-gray-800/60 focus:ring-2 focus:ring-emerald-500/25 transition-all outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm resize-none"
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowFeedbackModal(false)}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-800 font-semibold"
                >{t('groups.cancelar')}</button>
                <button
                  type="submit"
                  disabled={feedbackLoading || !feedbackMessage.trim()}
                  className="flex-1 px-4 py-3 text-white rounded-2xl font-black shadow-[0_4px_12px_rgba(0,0,0,0.15)] hover:shadow-[0_8px_20px_rgba(0,0,0,0.2)] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#055590ff' }}
                >
                  {feedbackLoading ? t('common.sending') : t('common.send')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <PageMeta
        title={t('common.reactjsSigninDashboardTailadminNextjsAdminDashboardTemplate')}
        description={t('common.signInPageDescription', {
          defaultValue: t('auth.iniciasesionparaaccederalpanel')
        })}
      />
      <AuthLayout right={SignInRightPanel}>
        <SignInForm />
      </AuthLayout>
    </>
  );
};

export default SignIn;
